import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Pagination } from "simplydo/react/ui/pagination";
import { Popup } from "simplydo/react/ui/popup";
import { TabPane } from "simplydo/react/ui/tab";
import { Table, TableBody } from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import Prompt from "utils/Prompt";
import util from "utils/utils";

import { UserChooser } from "components/lib/Choosers";
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";
import ChallengeIdeaFilters from "components/lib/Filters/ChallengeIdeaFilters";
import ChallengeIdeaFiltersButton from "components/lib/Filters/ChallengeIdeaFiltersButton";
import { HandledIdeaPreview } from "components/lib/Ideas/IdeaPreview";

import { AssessmentIdeaRow } from "./AssessmentIdeaRow";
import { AssessorRow } from "./AssessorRow";
import { AssessmentReviewModal } from "./AssignmentReviewModal";
import { IdeaInspectModal } from "./IdeaInspectModal";
import { UserInspectModal } from "./UserInspectModal";

export const TableHeaderItem = styled(H4)`
  img {
    height: 26px !important;
    width: 26px !important;
    margin-right: 8px !important;
  }
  div.image {
    margin-right: 5px !important;
  }
`;

const GridHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  > .header {
    margin: 0;
  }
`;

export type AssignableUser =
  OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["emails"][0] &
    OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["users"][0];

export type LocalAssignments = {
  [userId: string]: {
    user: AssignableUser;
    ideas: {
      [key: string]: {
        idea: OpenAPI.Schemas["Idea"];
        assessmentRoundIds: string[];
      };
    };
  };
};

type AssignmentDashboardProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
};

const AssignmentDashboard = ({ challenge }: AssignmentDashboardProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const challengeId = challenge?._id;

  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = useMemo(
    () => template?.assessments?.rounds || [],
    [template],
  );
  const assessmentRoundMap = useMemo(
    () => Object.fromEntries(assessmentRounds.map((r) => [r.id, r])),
    [assessmentRounds],
  );

  const ideaFilters = useAppSelector((state) => state.challengeIdeaFilters);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [previewIdea, setPreviewIdea] = useState<string | null>(null);

  // Local state for tracking draft USER assignments made before saving
  const [localUserAssignments, setLocalUserAssignments] =
    useState<LocalAssignments>({});
  const localUserAssignmentList = useMemo(
    () => Object.entries(localUserAssignments),
    [localUserAssignments],
  );

  // Local state for tracking draft EMAIL assignments made before saving
  const [localEmailAssignments, setLocalEmailAssignments] =
    useState<LocalAssignments>({});
  const localEmailAssignmentList = useMemo(
    () => Object.entries(localEmailAssignments),
    [localEmailAssignments],
  );

  // The count of assignments in draft to display in the UI
  const totalAssignmentCount = useMemo(() => {
    let assignmentCount = 0;
    localUserAssignmentList.forEach(([userId, assignment]) => {
      Object.values(assignment.ideas).forEach((assign) => {
        assignmentCount += assign.assessmentRoundIds.length;
      });
    });
    localEmailAssignmentList.forEach(([userId, assignment]) => {
      Object.values(assignment.ideas).forEach((assign) => {
        assignmentCount += assign.assessmentRoundIds.length;
      });
    });
    return assignmentCount;
  }, [localUserAssignmentList, localEmailAssignmentList]);

  // The state of which users/emails/ideas are selected in the UI before being assigned to the draft states above
  const [selectedUsers, setSelectedUsers] = useState<AssignableUser[]>([]);
  const selectedUserIds = selectedUsers.map((u) => u._id);
  const [selectedEmails, setSelectedEmails] = useState<AssignableUser[]>([]);
  const selectedEmailIds = selectedEmails.map((u) => u._id);

  const [selectedPotentialIdeas, setSelectedPotentialIdeas] = useState<
    OpenAPI.Schemas["Idea"][]
  >([]);
  const selectedPotentialIdeaIds = selectedPotentialIdeas.map((i) => i._id);

  // Any user or idea can be inspected, this inspection then shows the matrix of users/ideas who are currently draft assigned, and that are already assigned as assessors
  const [inspectedAssessor, setInspectedAssessor] =
    useState<AssignableUser | null>(null);
  const inspectedAssessorAssignments = useMemo(() => {
    if (!inspectedAssessor) {
      return null;
    }
    if (inspectedAssessor.isEmailInvitee) {
      return localEmailAssignments[inspectedAssessor.email]?.ideas;
    }
    return localUserAssignments[inspectedAssessor._id]?.ideas;
  }, [inspectedAssessor, localEmailAssignments, localUserAssignments]);

  const [inspectedIdea, setInspectedIdea] = useState<
    OpenAPI.Schemas["Idea"] | null
  >(null);
  // Reverse map of the local assignments for just the selected idea to show which users/emails are assigned
  const inspectedIdeaAssignments = useMemo(() => {
    if (!inspectedIdea) {
      return null;
    }
    const assignments: LocalAssignments = {};
    for (const [userId, assignment] of Object.entries(
      localUserAssignments || {},
    )) {
      const ideaEntry = assignment.ideas[inspectedIdea._id];
      if (ideaEntry) {
        assignments[userId] = {
          ...assignment,
          ideas: { [inspectedIdea._id]: ideaEntry },
        };
      }
    }
    for (const [email, assignment] of Object.entries(
      localEmailAssignments || {},
    )) {
      const ideaEntry = assignment.ideas[inspectedIdea._id];
      if (ideaEntry) {
        assignments[email] = {
          ...assignment,
          ideas: { [inspectedIdea._id]: ideaEntry },
        };
      }
    }
    return assignments;
  }, [inspectedIdea, localEmailAssignments, localUserAssignments]);

  // Starting here are the api queries to fetch assessor & idea data
  // First is a query to fetch assessors with pagination and search
  const [assessorSearchParams, setAssessorSearchParams] = useState({
    query: "",
    page: 1,
    includePotential: true,
  });
  const throttleAssessments = useThrottle(
    api.challenges.getAssessmentDashboard,
    400,
    [],
  );
  const assessmentDashboardQuery = useQuery(
    initUseQuery(throttleAssessments, [challenge?._id, assessorSearchParams], {
      placeholderData: (data: any) => data,
    }),
  );

  // Secondly here ideas are fetched with the challenge filters applied from the redux reducer
  const ideaFilterQuery = useQuery(
    initUseQuery(api.challenges.getIdeaFilters, [challenge?._id]),
  );
  const [potentialIdeaSearchSettings, setPotentialIdeaSearchSettings] =
    useState({ query: "", page: 1, limit: 15 });
  const fetchPotentialIdeas = useThrottle(api.challenges.getIdeas, 400, []);
  const ideasQuery = useQuery(
    initUseQuery(
      fetchPotentialIdeas,
      [
        challenge?._id,
        {
          ...potentialIdeaSearchSettings,
          tags: ideaFilters.tags,
          status: ideaFilters.status,
          sort: ideaFilters.sort,
          sortDirection: ideaFilters.sortDirection,
          labels: ideaFilters.labels,
          authorGroupIds: ideaFilters.authorGroupIds,
          authorOrganisationIds: ideaFilters.authorOrganisationIds,
          fieldChoices: ideaFilters.fieldChoices,
          hasAdminComment: ideaFilters.hasAdminComment,
          hasCustomDeadline: ideaFilters.hasCustomDeadline,
          assignees: ideaFilters.assignees,
          lanes: ideaFilters.lanes,
          impactIds: ideaFilters.impactIds,
          contentType: "assessment",
        },
      ],
      { placeholderData: (data: any) => data },
    ),
  );

  // We also construct a map of all ideas that are assessable for easy lookup in the inspect modals
  // Because of pagination we need to make sure that the idea data is always accessible from either the query or the local draft assignments. Otherwise changing a page might make data unavailable during inspection.
  const assessableIdeasById = useMemo(() => {
    const byId =
      assessmentDashboardQuery.data?.ideas?.reduce(
        (
          obj: { [key: string]: OpenAPI.Schemas["Idea"] },
          item: OpenAPI.Schemas["Idea"],
        ) => {
          obj[item._id] = item;
          return obj;
        },
        {} as { [key: string]: OpenAPI.Schemas["Idea"] },
      ) || {};

    Object.entries(localUserAssignments).forEach(([userId, assignment]) => {
      Object.entries(assignment.ideas).forEach(([ideaId, ideaAssignment]) => {
        byId[ideaId] = ideaAssignment.idea;
      });
    });
    Object.entries(localEmailAssignments).forEach(([userId, assignment]) => {
      Object.entries(assignment.ideas).forEach(([ideaId, ideaAssignment]) => {
        byId[ideaId] = ideaAssignment.idea;
      });
    });

    return byId;
  }, [
    assessmentDashboardQuery.data?.ideas,
    localUserAssignments,
    localEmailAssignments,
  ]);

  const addPotentialAssessors = useCallback(
    (userIds: string[], emails: string[]) => {
      api.challenges.addPotentialAssessors(
        challenge?._id,
        userIds,
        emails,
        () => {
          api.invalidateQueries(api.challenges.getAssessmentDashboard, [
            challenge?._id,
          ]);
        },
        () => {},
      );
    },
    [challenge?._id],
  );

  const removePotentialAssessors = useCallback(
    (userIds: string[], emails: string[]) => {
      api.challenges.removePotentialAssessors(
        challenge?._id,
        userIds,
        emails,
        () => {
          api.invalidateQueries(api.challenges.getAssessmentDashboard, [
            challenge?._id,
          ]);
        },
        () => {},
      );
    },
    [challenge?._id],
  );

  const searchAssessors = useCallback(
    (
      queryTerm: string,
      success: (users: OpenAPI.Schemas["User"][]) => void,
      fail: (err: any) => void,
    ) => {
      api.search.challengeAssessors(
        challenge?._id,
        queryTerm,
        ({ users }) => {
          success(users);
        },
        fail,
      );
    },
    [challenge?._id],
  );

  const unassignAssessor = useCallback(
    (ideaId: string, assessorId: string, assessmentRoundId: string) => {
      const round = assessmentRoundMap[assessmentRoundId];
      util
        .confirm(
          `Unassign assessor from assessment`,
          `Are you sure you want to unassign this assessor from ${round?.name}? Their existing assessment will be deleted. This action cannot be undone.`,
        )
        .then(() => {
          api.challenges.unassignAssessors(
            challengeId,
            [ideaId],
            [assessorId],
            assessmentRoundId,
            () => {
              api.invalidateQueries(api.challenges.getAssessmentDashboard, [
                challengeId,
              ]);
              api.invalidateQueries(api.challenges.getIdeas, [challengeId]);

              if (inspectedAssessor) {
                setInspectedAssessor((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    assignments: {
                      ...prev.assignments,
                      [ideaId]: {
                        assessmentRoundIds:
                          prev.assignments?.[ideaId]?.assessmentRoundIds.filter(
                            (id) => id !== assessmentRoundId,
                          ) || [],
                      },
                    },
                  };
                });
              }
              if (inspectedIdea) {
                setInspectedIdea((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    assessments:
                      prev.assessments?.filter((a) => {
                        return !(
                          a.user === assessorId &&
                          a.assessmentRoundId === assessmentRoundId
                        );
                      }) || [],
                  };
                });
              }
            },
            (err) => {
              toast.error(err.message);
            },
          );
        })
        .catch(() => {});
    },
    [challengeId, assessmentRoundMap, inspectedAssessor, inspectedIdea],
  );

  const reassignActiveAssessments = useCallback(
    (
      fromAssessorId: string,
      assignments: { ideaId: string; assessmentRoundId: string }[],
      target: AssignableUser,
    ) => {
      const count = assignments.length;
      const targetName = target.isEmailInvitee
        ? target.email || target._id
        : target.ownerAssessor?.profile?.fullName || "assessor";

      util
        .confirm(
          "Reassign assessments",
          `Reassign ${util.pluralise(count, "assessment", "assessments")} to ${targetName}? Any in-progress assessment data will be deleted and cannot be undone.`,
        )
        .then(() => {
          const ideasPayload: Record<string, { assessmentRoundIds: string[] }> =
            {};
          for (const { ideaId, assessmentRoundId } of assignments) {
            if (!ideasPayload[ideaId]) {
              ideasPayload[ideaId] = { assessmentRoundIds: [] };
            }
            if (
              !ideasPayload[ideaId]!.assessmentRoundIds.includes(
                assessmentRoundId,
              )
            ) {
              ideasPayload[ideaId]!.assessmentRoundIds.push(assessmentRoundId);
            }
          }

          const assignmentPayload = target.isEmailInvitee
            ? {
                userAssignments: {},
                emailAssignments: {
                  [target.email || target._id]: { ideas: ideasPayload },
                },
              }
            : {
                userAssignments: { [target._id]: { ideas: ideasPayload } },
                emailAssignments: {},
              };

          const assignToTarget = () => {
            api.challenges.assignAssessorAssignments(
              challengeId,
              assignmentPayload,
              () => {
                toast.success(
                  `Reassigned ${util.pluralise(count, "assessment", "assessments")}`,
                );
                api.invalidateQueries(api.challenges.getAssessmentDashboard, [
                  challengeId,
                ]);
                api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
                api.invalidateQueries(api.challenges.getAssignedAssessments, [
                  challengeId,
                ]);

                setInspectedAssessor((prev) => {
                  if (!prev) return prev;
                  const newAssignments = { ...prev.assignments };
                  for (const { ideaId, assessmentRoundId } of assignments) {
                    const existing =
                      newAssignments[ideaId]?.assessmentRoundIds || [];
                    newAssignments[ideaId] = {
                      assessmentRoundIds: existing.filter(
                        (id) => id !== assessmentRoundId,
                      ),
                    };
                  }
                  return { ...prev, assignments: newAssignments };
                });
              },
              (err) => {
                toast.error(err.message);
              },
            );
          };

          const unassignNext = (index: number) => {
            if (index >= assignments.length) {
              assignToTarget();
              return;
            }
            const { ideaId, assessmentRoundId } = assignments[index]!;
            api.challenges.unassignAssessors(
              challengeId,
              [ideaId],
              [fromAssessorId],
              assessmentRoundId,
              () => unassignNext(index + 1),
              (err) => {
                toast.error(err?.message || "Failed to reassign assessments");
              },
            );
          };

          unassignNext(0);
        })
        .catch(() => {});
    },
    [challengeId],
  );

  const assignLocalUsersAndEmailsToRound = useCallback(
    (roundId: string) => {
      setLocalUserAssignments((prev) => {
        const newState = { ...prev };
        for (const assessor of selectedUsers) {
          newState[assessor._id] = {
            user: assessor,
            ideas: { ...(newState[assessor._id]?.ideas || {}) },
          };
          const assessorEntry = newState[assessor._id]!;

          for (const idea of selectedPotentialIdeas) {
            const existingRoundIds =
              assessorEntry.ideas[idea._id]?.assessmentRoundIds || [];
            const newRoundIds = existingRoundIds.includes(roundId)
              ? existingRoundIds
              : [...existingRoundIds, roundId];
            assessorEntry.ideas[idea._id] = {
              idea: idea,
              assessmentRoundIds: newRoundIds,
            };
          }
        }
        return newState;
      });
      setLocalEmailAssignments((prev) => {
        const newState = { ...prev };
        for (const email of selectedEmails) {
          newState[email.email] = {
            user: email,
            ideas: { ...(newState[email.email]?.ideas || {}) },
          };
          const emailEntry = newState[email.email]!;

          for (const idea of selectedPotentialIdeas) {
            const existingRoundIds =
              emailEntry.ideas[idea._id]?.assessmentRoundIds || [];
            const newRoundIds = existingRoundIds.includes(roundId)
              ? existingRoundIds
              : [...existingRoundIds, roundId];
            emailEntry.ideas[idea._id] = {
              idea: idea,
              assessmentRoundIds: newRoundIds,
            };
          }
        }
        return newState;
      });
      setSelectedUsers([]);
      setSelectedEmails([]);
      setSelectedPotentialIdeas([]);
    },
    [selectedUsers, selectedEmails, selectedPotentialIdeas],
  );

  return (
    <Container fluid>
      <Prompt
        when={
          localUserAssignmentList.length > 0 ||
          localEmailAssignmentList.length > 0
        }
        message="You have unsaved draft assignments. Are you sure you want to leave? This will discard your unsaved changes."
        shouldBlock={(e) => {
          return e.currentLocation.pathname !== e.nextLocation.pathname;
        }}
      />
      <HandledIdeaPreview
        ideaId={previewIdea}
        onCloseModal={() => setPreviewIdea(null)}
      />
      <ChallengeIdeaFilters
        challenge={challenge}
        popularTags={ideaFilterQuery.data?.popularTags || []}
        allLabels={ideaFilterQuery.data?.allLabels || []}
        availableGroups={ideaFilterQuery.data?.availableGroups ?? []}
        availableOrganisations={
          ideaFilterQuery.data?.availableOrganisations || []
        }
        availableSortParameters={
          ideaFilterQuery.data?.availableSortParameters || {}
        }
        availableFilterParameters={
          ideaFilterQuery.data?.availableFilterParameters || {}
        }
        allProjectAssignees={
          (ideaFilterQuery.data?.allProjectAssignees || []) as any
        }
        availableImpacts={ideaFilterQuery.data?.availableImpacts || []}
      />
      <AssessmentReviewModal
        localUserAssignments={localUserAssignments}
        localEmailAssignments={localEmailAssignments}
        open={reviewOpen}
        setOpen={setReviewOpen}
        challenge={challenge}
        onSave={() => {
          setLocalUserAssignments({});
          setLocalEmailAssignments({});
        }}
      />
      <UserInspectModal
        challengeId={challengeId}
        inspectedAssessor={inspectedAssessor}
        setInspectedAssessor={setInspectedAssessor}
        localAssignments={inspectedAssessorAssignments}
        setLocalAssignments={
          inspectedAssessor?.isEmailInvitee
            ? setLocalEmailAssignments
            : setLocalUserAssignments
        }
        assessableIdeasById={assessableIdeasById}
        unassignAssessor={unassignAssessor}
        reassignActiveAssessments={reassignActiveAssessments}
        dashboardUsers={
          (assessmentDashboardQuery.data?.users as AssignableUser[]) || []
        }
        dashboardEmails={
          (assessmentDashboardQuery.data?.emails as AssignableUser[]) || []
        }
        assessmentRoundMap={assessmentRoundMap}
      />
      <IdeaInspectModal
        inspectedIdea={inspectedIdea}
        setInspectedIdea={setInspectedIdea}
        localAssignments={inspectedIdeaAssignments}
        setLocalUserAssignments={setLocalUserAssignments}
        setLocalEmailAssignments={setLocalEmailAssignments}
        unassignAssessor={unassignAssessor}
        assessmentRoundMap={assessmentRoundMap}
      />

      <div
        style={{
          position: "absolute",
          top: 16,
          right: 24,
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          alignItems: "center",
          gap: 8,
          zIndex: 10,
        }}
      >
        <ButtonGroup size="small">
          <Popup
            trigger={
              assessmentRounds?.length > 1 ? (
                <Dropdown
                  disabled={
                    (selectedUsers.length === 0 &&
                      selectedEmails.length === 0) ||
                    selectedPotentialIdeas.length === 0
                  }
                  trigger={
                    <Button
                      color={
                        (selectedUsers.length !== 0 ||
                          selectedEmails.length !== 0) &&
                        selectedPotentialIdeas.length !== 0
                          ? "secondary"
                          : undefined
                      }
                    >
                      {selectedUsers.length === 0 &&
                      selectedEmails.length === 0 &&
                      selectedPotentialIdeas.length === 0
                        ? util.pluralise(
                            totalAssignmentCount,
                            "assignment",
                            "assignments",
                          )
                        : `Add ${util.pluralise(selectedUsers.length + selectedEmails.length, "user", "users")} & ${util.pluralise(selectedPotentialIdeaIds.length, t("generic.idea"), t("generic.ideas"))} to..`}
                    </Button>
                  }
                  direction="left"
                >
                  <DropdownMenu>
                    {assessmentRounds?.map((round) => (
                      <DropdownItem
                        key={round.id}
                        onClick={() =>
                          assignLocalUsersAndEmailsToRound(round.id)
                        }
                      >
                        {round.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              ) : (
                <Button
                  onClick={() =>
                    assignLocalUsersAndEmailsToRound(assessmentRounds[0]!.id)
                  }
                  disabled={
                    (selectedUsers.length === 0 &&
                      selectedEmails.length === 0) ||
                    selectedPotentialIdeas.length === 0
                  }
                  color={
                    (selectedUsers.length !== 0 ||
                      selectedEmails.length !== 0) &&
                    selectedPotentialIdeas.length !== 0
                      ? "secondary"
                      : undefined
                  }
                >
                  {selectedUsers.length === 0 &&
                  selectedEmails.length === 0 &&
                  selectedPotentialIdeas.length === 0
                    ? util.pluralise(
                        totalAssignmentCount,
                        "assignment",
                        "assignments",
                      )
                    : `Add ${util.pluralise(selectedUsers.length + selectedEmails.length, "user", "users")} & ${util.pluralise(selectedPotentialIdeaIds.length, t("generic.idea"), t("generic.ideas"))}`}
                </Button>
              )
            }
          >{`Locally assign assessors to ${t("generic.ideas")}. This will not be saved until you click "Save assignments".`}</Popup>
          <Button
            disabled={
              localUserAssignmentList.length === 0 &&
              localEmailAssignmentList.length === 0
            }
            onClick={() => {
              util
                .confirm(
                  "Clear all draft assignments",
                  `Are you sure you want to clear all draft assignments? There are currently ${util.pluralise(localUserAssignmentList.length + localEmailAssignmentList.length, "user", "users")} with unsaved draft assignments.`,
                )
                .then(() => {
                  setLocalUserAssignments({});
                  setLocalEmailAssignments({});
                })
                .catch(() => {});
            }}
          >
            <Icon icon={Trash2Icon} />
          </Button>
        </ButtonGroup>

        <Button
          size="small"
          disabled={
            localUserAssignmentList.length === 0 &&
            localEmailAssignmentList.length === 0
          }
          onClick={() => {
            setReviewOpen(true);
          }}
          color={
            localUserAssignmentList.length > 0 ||
            localEmailAssignmentList.length > 0
              ? "primary"
              : undefined
          }
        >
          Review & assign
        </Button>
      </div>
      <Grid columns={2} style={{ marginBottom: 10 }}>
        <GridColumn>
          <GridHeader>
            <H3>Assessor audience</H3>
          </GridHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div>
                <UserChooser
                  clearOnComplete
                  trigger={
                    <Button size="small" color="secondary">
                      Add assessors
                    </Button>
                  }
                  onComplete={(newPotentialAssessors) => {
                    const newUserIds: string[] = [];
                    const newEmails: string[] = [];
                    for (const newPotentialAssessor of newPotentialAssessors) {
                      const addingCollection =
                        newPotentialAssessor.isEmailInvitee
                          ? newEmails
                          : newUserIds;
                      if (
                        !addingCollection.includes(newPotentialAssessor._id)
                      ) {
                        addingCollection.push(newPotentialAssessor._id);
                      }
                    }
                    addPotentialAssessors(newUserIds, newEmails);
                  }}
                  searchFunction={searchAssessors}
                  forType="assessor"
                  forId={challenge?._id}
                  afterContent={() =>
                    !challenge?.visibility?.organisations?.includes(
                      user!.organisation,
                    ) ? (
                      <AssessorAudienceWarning />
                    ) : null
                  }
                />
                <ButtonGroup size="small">
                  <Button
                    onClick={() => {
                      setSelectedUsers((prevState) => {
                        const newState = [...prevState];
                        const ids = newState.map((u) => u._id);
                        for (const user of assessmentDashboardQuery?.data
                          ?.users || []) {
                          if (!ids.includes(user._id)) {
                            newState.push(user as AssignableUser);
                          }
                        }
                        return newState;
                      });
                      setSelectedEmails((prevState) => {
                        const newState = [...prevState];
                        const ids = newState.map((email) => email.email);
                        for (const email of assessmentDashboardQuery?.data
                          ?.emails || []) {
                          if (!ids.includes(email.email)) {
                            newState.push(email as AssignableUser);
                          }
                        }
                        return newState;
                      });
                    }}
                  >
                    Select all on page
                  </Button>
                  {selectedUsers?.length > 0 || selectedEmails?.length > 0 ? (
                    <Button
                      onClick={() => {
                        setSelectedUsers([]);
                        setSelectedEmails([]);
                      }}
                    >
                      Deselect all
                    </Button>
                  ) : null}
                </ButtonGroup>
              </div>
            </div>

            <TabPane>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <Input
                  value={assessorSearchParams.query}
                  onChange={(e) => {
                    setAssessorSearchParams((prevState) => ({
                      ...prevState,
                      page: 1,
                      query: e.target.value,
                    }));
                  }}
                  loading={assessmentDashboardQuery.isFetching}
                  placeholder={`Search assessors...`}
                />
                {assessmentDashboardQuery.data?.previousPageAvailable ||
                assessmentDashboardQuery.data?.nextPageAvailable ? (
                  <div>
                    <Pagination
                      onPageChange={(_e, { activePage }) =>
                        setAssessorSearchParams((prev) => ({
                          ...prev,
                          page: activePage as number,
                        }))
                      }
                      activePage={assessorSearchParams.page}
                      totalPages={Math.ceil(
                        (assessmentDashboardQuery?.data.total || 0) / 25,
                      )}
                      ellipsisItem={null}
                      firstItem={null}
                      lastItem={null}
                    />
                  </div>
                ) : null}
              </div>
              {assessmentDashboardQuery.data?.users?.length > 0 ? (
                <Table fixed>
                  <TableBody>
                    {(
                      assessmentDashboardQuery.data?.users as
                        | OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["users"]
                        | undefined
                    )?.map((item) => {
                      return (
                        <AssessorRow
                          key={item._id}
                          item={item as AssignableUser}
                          localAssignments={
                            localUserAssignments[item._id]?.ideas
                          }
                          onInspect={setInspectedAssessor}
                          isSelected={selectedUserIds.includes(item._id)}
                          onSelect={() => {
                            setSelectedUsers((prevState) => {
                              const ids = prevState.map((u) => u._id);
                              if (ids.includes(item._id)) {
                                return prevState.filter(
                                  (u) => u._id !== item._id,
                                );
                              } else {
                                return [...prevState, item as AssignableUser];
                              }
                            });
                          }}
                          removeAssessor={(userId) => {
                            setSelectedUsers((prevState) =>
                              prevState.filter((u) => u._id !== item._id),
                            );
                            removePotentialAssessors([userId], []);
                          }}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              ) : null}
              {assessmentDashboardQuery.data?.emails?.length > 0 ? (
                <>
                  <H4>Invited via email</H4>
                  <Table fixed>
                    <TableBody>
                      {(
                        assessmentDashboardQuery.data?.emails as
                          | OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["emails"]
                          | undefined
                      )?.map((item) => {
                        return (
                          <AssessorRow
                            key={item._id}
                            item={item as AssignableUser}
                            localAssignments={
                              localEmailAssignments[item._id]?.ideas
                            }
                            onInspect={setInspectedAssessor}
                            isSelected={selectedEmailIds.includes(item._id)}
                            onSelect={() => {
                              setSelectedEmails((prevState) => {
                                const ids = prevState.map((u) => u._id);
                                if (ids.includes(item._id)) {
                                  return prevState.filter(
                                    (u) => u._id !== item._id,
                                  );
                                } else {
                                  return [...prevState, item as AssignableUser];
                                }
                              });
                            }}
                            removeAssessor={(email) => {
                              setSelectedEmails((prevState) =>
                                prevState.filter((u) => u._id !== item._id),
                              );
                              removePotentialAssessors([], [email]);
                            }}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              ) : null}
            </TabPane>
          </div>
        </GridColumn>
        <GridColumn>
          <GridHeader>
            <H3>Available {t("generic.ideas")}</H3>
          </GridHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div>
                <ChallengeIdeaFiltersButton
                  color="secondary"
                  variant="regular"
                  showIcon={false}
                />
                <ButtonGroup size="small">
                  <Button
                    onClick={() => {
                      setSelectedPotentialIdeas((prevState) => {
                        const newState = [...prevState];
                        const ids = newState.map((u) => u._id);
                        for (const idea of ideasQuery?.data?.ideas || []) {
                          if (!ids.includes(idea._id)) {
                            newState.push(idea);
                          }
                        }
                        return newState;
                      });
                    }}
                  >
                    Select all on page
                  </Button>
                  {selectedPotentialIdeas?.length > 0 ? (
                    <Button onClick={() => setSelectedPotentialIdeas([])}>
                      Deselect all
                    </Button>
                  ) : null}
                </ButtonGroup>
              </div>
            </div>

            <TabPane>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <Input
                  value={potentialIdeaSearchSettings.query}
                  onChange={(e) => {
                    setPotentialIdeaSearchSettings((prevState) => ({
                      ...prevState,
                      page: 1,
                      query: e.target.value,
                    }));
                  }}
                  loading={ideasQuery.isFetching}
                  placeholder={`Search ${t("generic.ideas")}...`}
                />
                {ideasQuery.data?.previousPageAvailable ||
                ideasQuery.data?.nextPageAvailable ? (
                  <div>
                    <Pagination
                      onPageChange={(_e, { activePage }) =>
                        setPotentialIdeaSearchSettings((prev) => ({
                          ...prev,
                          page: activePage as number,
                        }))
                      }
                      activePage={potentialIdeaSearchSettings.page}
                      totalPages={Math.ceil(
                        (ideasQuery?.data.total || 0) /
                          potentialIdeaSearchSettings.limit,
                      )}
                      ellipsisItem={null}
                      firstItem={null}
                      lastItem={null}
                    />
                  </div>
                ) : null}
              </div>
              {ideasQuery?.data?.ideas?.length ? (
                <Table fixed>
                  <TableBody>
                    {(
                      ideasQuery?.data?.ideas as
                        | OpenAPI.Schemas["Idea"][]
                        | undefined
                    )?.map((idea) => (
                      <AssessmentIdeaRow
                        key={idea._id}
                        idea={idea}
                        emailInvites={assessmentDashboardQuery.data?.emails}
                        localUserAssignments={localUserAssignments}
                        localEmailAssignments={localEmailAssignments}
                        onInspect={setInspectedIdea}
                        onPreview={() => setPreviewIdea(idea._id)}
                        isSelected={selectedPotentialIdeaIds.includes(idea._id)}
                        onSelect={() => {
                          setSelectedPotentialIdeas((prevState) => {
                            const ids = prevState.map((i) => i._id);
                            if (ids.includes(idea._id)) {
                              return prevState.filter(
                                (i) => i._id !== idea._id,
                              );
                            } else {
                              return [...prevState, idea];
                            }
                          });
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              ) : null}
            </TabPane>
          </div>
        </GridColumn>
      </Grid>
    </Container>
  );
};

export default AssignmentDashboard;
